import { createFetchAdapter, createHqApi } from '@hq/api-sdk';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface HqRuntimeConfig {
  /** 主业务 API 根（含 `/api` 段）。 */
  apiBase: string;
  /** 资源服务根（含 `/res` 段）。 */
  resBase: string;
  appTitle: string;
  uploadMaxMb: number;
  enableDebug: boolean;
  /**
   * 「手机打开」二维码里指向的站点根（手机浏览器需能访问到）。
   * 留空则用当前访问地址（`location.origin`）——生产建议显式配置成对外域名，
   * 避免在 `http://localhost:5010` 打开的页面把 localhost 编进二维码，手机无法访问。
   */
  mobileEntryUrl?: string;
}

export interface HqProfile {
  userId: string;
  orgId: string;
  /** 机构名称（后端下发；旧会话可能没有）。 */
  orgName?: string | null;
  deptId: string;
  name: string;
  /** 账号等级，如 L3（DR-8.6）。 */
  level: string;
  /** 后端下发的权限码；undefined 表示旧会话/开发态发号，不做前端菜单裁剪。 */
  perms?: string[];
  /** 个人账户：工号、科室、头像、脱敏手机、邮箱。 */
  userCode?: string | null;
  deptName?: string | null;
  avatarUrl?: string | null;
  mobile?: string | null;
  email?: string | null;
}

declare global {
  interface Window {
    /** 部署时由 public/config.js 注入；缺失时回退默认值。 */
    __HQ_CONFIG__?: Partial<HqRuntimeConfig>;
  }
}

const fallback: HqRuntimeConfig = {
  apiBase: '/api',
  resBase: '/res',
  appTitle: '物资平台 · 系统管理',
  uploadMaxMb: 50,
  enableDebug: false,
  mobileEntryUrl: ''
};

/** 本机 dev 强制同源，走 Vite 反代（5010→5001/5002）；生产绝对地址由 config.js 提供。 */
const devOverride: Partial<HqRuntimeConfig> = import.meta.env.DEV ? { apiBase: '/api', resBase: '/res' } : {};

/** 运行时配置：`public/config.js` 覆盖默认值，同一份产物可跨环境部署。 */
const runtime: HqRuntimeConfig = { ...fallback, ...(window.__HQ_CONFIG__ ?? {}), ...devOverride };

export const config: HqRuntimeConfig = runtime;

export const appTitle = config.appTitle;

/** 账号等级中文名收敛在 `@hq/utils`，此处 re-export 保持端内既有 import 不变。 */
export { LEVEL_LABELS, levelLabel } from '@hq/utils';

/**
 * 代管状态（docs/07 §7.4 `POST /api/sys/tenant/{id}/enter`）：
 * 持有 15 分钟只读令牌，进入期间所有请求都以「被进入租户」的视角发出，
 * `returnToken` 保存原平台令牌，退出时还原。
 */
export interface HqEnterState {
  token: string;
  tenantId: string;
  tenantName: string;
  /** 过期时间戳（毫秒）。 */
  expiresAt: number;
  returnToken: string;
}

interface AuthState {
  token: string | null;
  profile: HqProfile | null;
  enter: HqEnterState | null;
  setSession: (token: string, profile: HqProfile) => void;
  /** 会话局部更新（头像/科室等由 /auth/me 补全）。 */
  patchProfile: (patch: Partial<HqProfile>) => void;
  /** 进入租户视角：把当前令牌换成只读代管令牌。 */
  startEnter: (params: { token: string; tenantId: string; tenantName: string; minutes: number }) => void;
  /** 退出代管：还原平台令牌。 */
  endEnter: () => void;
  clear: () => void;
}

/** Web 端令牌存内存 + sessionStorage（docs/05 §6-1），关浏览器即失效。 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      profile: null,
      enter: null,
      setSession: (token, profile) => set({ token, profile, enter: null }),
      patchProfile: (patch) =>
        set((state) => ({ profile: state.profile ? { ...state.profile, ...patch } : null })),
      startEnter: ({ token, tenantId, tenantName, minutes }) =>
        set((state) => ({
          token,
          enter: {
            token,
            tenantId,
            tenantName,
            expiresAt: Date.now() + minutes * 60_000,
            returnToken: state.token ?? ''
          }
        })),
      endEnter: () =>
        set((state) => ({
          // 平台令牌丢失（例如清过 sessionStorage）时退回登录页，避免带着只读令牌继续操作
          token: state.enter?.returnToken || null,
          enter: null
        })),
      clear: () => set({ token: null, profile: null, enter: null })
    }),
    {
      // 各站点登录态互不共享：key 带门户名
      name: 'hq-system-auth',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);

const getToken = (): string | null => useAuthStore.getState().token;

/**
 * 三端共用的 SDK 实例：注入 fetch 适配器、令牌来源与 401 处理。
 * 业务页面只依赖 `hq.*` 命名空间，不直接碰 fetch。
 */
export const hq = createHqApi({
  adapter: createFetchAdapter(),
  baseUrl: config.apiBase,
  resBaseUrl: config.resBase,
  resolveToken: getToken,
  onUnauthorized: () => useAuthStore.getState().clear()
});
